import React, { useMemo } from 'react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';

interface SparklinePoint {
  date: string;
  equity: number;
}

interface StrategySparklineProps {
  data: SparklinePoint[];
  height?: number;
}

/**
 * Tiny axis-less equity-curve sparkline used on the landing-page strategy
 * cards. Fed by `PerformanceStats.dailyEquity`. Rendered only after the card's
 * backtest is revealed (compliance gating lives in the parent), so this is a
 * pure presentational component.
 *
 * Color follows direction: green when the curve ends above where it started,
 * red otherwise — matching the up/down convention used elsewhere in the app.
 */
const StrategySparkline: React.FC<StrategySparklineProps> = ({ data, height = 56 }) => {
  const up = useMemo(() => {
    if (!data || data.length < 2) return true;
    return data[data.length - 1].equity >= data[0].equity;
  }, [data]);

  if (!data || data.length < 2) return null;

  const stroke = up ? '#16a34a' : '#dc2626'; // green-600 / red-600
  const gradientId = up ? 'spark-up' : 'spark-down';

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity={0.25} />
            <stop offset="100%" stopColor={stroke} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          type="monotone"
          dataKey="equity"
          stroke={stroke}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          isAnimationActive={false}
          dot={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default StrategySparkline;
